import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero, CTABand, Rise } from "@/components/chrome";
import { INSIGHTS } from "@/data/insights";
import { findInsight, paths } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

export default function InsightPage() {
  const { insightSlug } = useParams();
  const post = findInsight(insightSlug);
  if (!post) return <NotFound />;
  const related = INSIGHTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div data-testid="insight-page">
      <PageHero prefix="insight"
        crumbs={[{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }, { name: post.category }]}
        eyebrow={`${post.category} · ${post.readMinutes} min read · ${new Date(post.date).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}`}
        title={post.title} lead={post.excerpt} />

      <article className="mx-auto max-w-3xl px-5 py-14 sm:py-16" data-testid="insight-article">
        {post.sections.map((sec, i) => (
          <Rise key={sec.h} className={i > 0 ? "mt-12" : ""}>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{sec.h}</h2>
            <div className="mt-4 space-y-4">
              {sec.paras.map((p, j) => <p key={j} className="leading-relaxed text-slate-600">{p}</p>)}
            </div>
          </Rise>
        ))}
        <div className="mt-14 rounded-2xl border border-cyan-200 bg-cyan-50 p-7" data-testid="insight-inline-cta">
          <p className="font-display text-lg font-semibold text-slate-900">Ready when you are.</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Tbay Cleaning Services serves 150+ Canadian cities. Call <a href="tel:+18076975399" className="font-semibold text-cyan-800 underline">807 697-5399</a> or
            email <a href="mailto:admin@tbaycleaners.com" className="font-semibold text-cyan-800 underline">admin@tbaycleaners.com</a> for a same-day quote.
          </p>
        </div>
        <Link to="/insights" data-testid="insight-back-link"
          className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 transition-colors hover:text-cyan-800">
          <ArrowLeft className="h-4 w-4" /> Back to all insights
        </Link>
      </article>

      <section className="mx-auto max-w-6xl px-5 pb-6" data-testid="insight-related">
        <div className="grid gap-5 md:grid-cols-2">
          {related.map((p) => (
            <Link key={p.slug} to={paths.insight(p.slug)} data-testid={`related-insight-${p.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-700">{p.category} · {p.readMinutes} min read</p>
              <h3 className="mt-3 font-display text-lg font-semibold leading-snug tracking-tight text-slate-900">{p.title}</h3>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700">
                Read article <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <CTABand prefix="insight-cta" />
    </div>
  );
}
